import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import type { DxcIconName } from '../dxc.types';
import { DxcIconComponent } from '../icon/dxc-icon.component';

/** La severidad del aviso. Sale del mapa de §5; sin tono es un aviso neutro. */
export type DxcAlertTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * La forma del aviso.
 *
 *   `inline`  la de siempre: el glifo pequeño delante del texto.
 *   `plate`   el glifo del asunto a tamaño de ilustración, de marca de agua en
 *             un canto —la gramática de `.dxc-card--parallax`—. Para el aviso
 *             que encabeza una pantalla y dice DE QUÉ se trata, no solo cuánto
 *             urge.
 */
export type DxcAlertVariant = 'inline' | 'plate';

const DEFAULT_ICON: Record<DxcAlertTone, DxcIconName> = {
    info: 'info',
    success: 'check-circle',
    warning: 'alert-circle',
    danger: 'x-circle',
    neutral: 'info',
};

/**
 * Alerta: persistente y dentro del flujo (§6). El anfitrión ES el `.dxc-alert`,
 * así que las clases de colocación de la vista (`class="…__alert"`) caen sobre
 * la caja y no sobre un envoltorio.
 *
 * El texto va proyectado; las acciones, en `[dxcAlertActions]`, quedan al final
 * de la fila. Existe porque la alerta se componía a mano en cada vista —glifo,
 * `<b>`, `__text`— y la forma con marca de agua habría sido un cuarto andamio
 * que repetir.
 *
 * @example
 * <dxc-alert tone="warning" variant="plate" icon="factura" heading="Tienes cartas porte por timbrar">
 *   25 fletes asignados del periodo no tienen carta porte timbrada.
 *   <a dxcAlertActions class="dxc-btn dxc-btn--primary dxc-btn--sm" routerLink="…">Timbrar</a>
 * </dxc-alert>
 */
@Component({
    standalone: true,
    selector: 'dxc-alert',
    imports: [DxcIconComponent],
    template: `
        @if (variant === 'plate') {
            <span class="dxc-alert__plate" aria-hidden="true"><dxc-icon [name]="resolvedIcon"></dxc-icon></span>
        } @else if (showIcon) {
            <dxc-icon [name]="resolvedIcon"></dxc-icon>
        }
        <div class="dxc-alert__body">
            @if (heading) { <b class="dxc-alert__title">{{ heading }}</b> }
            <span class="dxc-alert__text"><ng-content></ng-content></span>
        </div>
        <span class="dxc-alert__actions"><ng-content select="[dxcAlertActions]"></ng-content></span>
        @if (dismissible) {
            <button type="button" class="dxc-alert__close" [attr.aria-label]="dismissLabel" (click)="dismissed.emit()">
                <dxc-icon name="close"></dxc-icon>
            </button>
        }
    `,
    host: {
        'class': 'dxc-alert',
        '[class.dxc-alert--info]': "tone === 'info'",
        '[class.dxc-alert--success]': "tone === 'success'",
        '[class.dxc-alert--warning]': "tone === 'warning'",
        '[class.dxc-alert--danger]': "tone === 'danger'",
        '[class.dxc-alert--plate]': "variant === 'plate'",
        '[class.dxc-alert--plate-left]': "variant === 'plate' && iconSide === 'left'",
        '[class.dxc-alert--enter]': 'enter',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcAlertComponent {

    @Input() tone: DxcAlertTone = 'info';
    @Input() variant: DxcAlertVariant = 'inline';
    /**
     * El título, en la tinta del tono. El cuerpo va proyectado. No se llama
     * `title`: un atributo `title` estático se queda además en el elemento, y
     * la alerta entera enseñaría su propio título como etiqueta al apuntarla.
     */
    @Input() heading = '';
    /** Glifo del aviso. Si se omite sale el de la severidad. */
    @Input() icon?: DxcIconName;
    /** `inline` sin glifo: para el aviso que solo lleva texto y una acción. */
    @Input() showIcon = true;
    /** En qué canto va la marca de agua de `plate`. En `inline` el glifo siempre encabeza. */
    @Input() iconSide: 'left' | 'right' = 'right';
    /**
     * Entrada que avisa: la alerta baja a su sitio, se enciende un instante en
     * su tono y late dos veces. Una alerta aparece porque algo cambió, y sin
     * movimiento una caja que nace en mitad de la página pasa desapercibida.
     * Se apaga (`[enter]="false"`) en la que ya estaba ahí al cargar una
     * pantalla donde no hay nada que anunciar.
     */
    @Input() enter = true;
    @Input() dismissible = false;
    @Input() dismissLabel = 'Cerrar aviso';

    /** La alerta no se quita sola: quién sabe si puede cerrarse es la vista. */
    @Output() dismissed = new EventEmitter<void>();

    get resolvedIcon(): DxcIconName {
        return this.icon ?? DEFAULT_ICON[this.tone];
    }
}
